import { Spinner, Tag } from "@blueprintjs/core";
import moment from "moment";
import {
  FGCustomInput,
  FGCustomPanel,
  FGEmpty,
  FGListen,
  FGNumberInput,
  FGSelectInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import * as Yup from "yup";

import { CompetitionApi, CompetitionDto, CompetitionRequestApi, CountryApi } from "../../../../api";
import {
  EmailButton,
  FGCountrySelect,
  FGPeopleSearchInput,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { CompetitionJudgeSelection } from "../../../request/competitionRequest/item/CompetitionJudgeSelection";
import { CompetitionResultList } from "./CompetitionResultList";

export interface ICompetitionDetailProps {
  competitionId: number;
  editMode: boolean;
}

export const CompetitionDetail: React.FunctionComponent<ICompetitionDetailProps> = ({ competitionId, editMode }) => {
  const { t } = useTl();
  const history = useHistory();
  const { openNewTab } = useWindow();
  const { currentLanguage } = useGlobalData();

  const api = useApiService(CompetitionApi);
  const requestApi = useApiService(CompetitionRequestApi);
  const countryApi = useApiService(CountryApi);

  const [otherCityChecked, setOtherCityChecked] = React.useState<boolean>();
  const [isBelgium, setIsBelgium] = React.useState<boolean>(false);
  const [transientJudgeError] = React.useState<boolean>(false);

  const [cities, pcloading] = useReferential((a) => a.getPostalCodes(), true, []);
  const [clubs, cloading] = useReferential((a) => a.getClubs(null), true, []);
  const [competitionStatuses, csloading] = useReferential((a) => a.getCompetitionStatuses(), true, []);
  const [sections, sloading] = useReferential((a) => a.getSections(), true, []);
  const [activities, aloading] = useReferential((a) => a.getActivities(null), true, []);
  const [subActivities, saloading] = useReferential((a) => a.getSubActivities(null), true, []);
  //===================== Getting Data ===========================

  const { data, loading, saving, saveItem } = useCrudApi<CompetitionDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = competitionId) => api.get(id, currentLanguage.languageId),
        saveApiFn: (d: CompetitionDto) => api.save(d),
        onSavedRoute: (d) => `${ERoutes.competition}/${d.competitionId}/detail/edit`,
        deleteApiFn: (d) => {},
      }),
      [api, competitionId, currentLanguage.languageId]
    ),
    [competitionId]
  );

  //======================== Data Manipulation ==============================

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      competitionRequest: Yup.object().shape({
        dateFrom: Yup.date()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("date-test", t(ETLCodes.DateMustBeSmallerThanEndDate), async function (value: Date) {
            if (this.parent.dateTo) {
              return value <= this.parent.dateTo;
            }
          }),
        dateTo: Yup.date()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("date-test", t(ETLCodes.DateMustBeGreaterThanStartDate), async function (value: Date) {
            if (this.parent.dateFrom && value) {
              return value >= this.parent.dateFrom;
            }
          }),
        countryId: Yup.number()
          .nullable()
          .test("is-required", t(ETLCodes.Required), function (value) {
            if (this.parent.otherAddress) {
              return !!value;
            }
            return true;
          }),
        postalCodeId: Yup.number()
          .nullable()
          .test("is-required", t(ETLCodes.Required), function (value) {
            if (this.parent.countryId && isBelgium) {
              return !!value;
            }
            return true;
          }),
        foreignCity: Yup.string()
          .nullable()
          .test("is-required", t(ETLCodes.Required), function (value) {
            if (this.parent.countryId && !isBelgium) {
              return !!value;
            }
            return true;
          }),
        request: Yup.object().shape({
          personId: Yup.number()
            .nullable()
            .required(t(ETLCodes.Required))
            .test("is-of-club", t(ETLCodes.PersonIsNotOfClub), async function (value) {
              if (data?.competitionRequest.clubId) {
                const res = await requestApi.competitionPersonCheck(data?.competitionRequest.clubId, value);
                return res.value;
              }
              return true;
            }),
        }),
      }),
    });
  }, [data, isBelgium, requestApi, t]);

  const onCancel = React.useCallback(() => history.push(`${ERoutes.competition}`), [history]);

  //============================= Form ==========================
  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveItem}
        validationSchema={FormSchema}
        loading={loading}
        saving={saving}
        onCancel={onCancel}
      >
        <FGListen field="competitionRequest.otherAddress" onChanged={(value) => setOtherCityChecked(value)} />
        <FGListen
          field="competitionRequest.countryId"
          onChanged={async (value) => {
            if (!!value) {
              const res = await countryApi.isBelgian(value);
              setIsBelgium(res.value);
            }
          }}
        />
        <FieldSet
          title={t(ETLCodes.Competition)}
          rightElement={
            <div style={{ display: "flex" }}>
              {data?.isImporting && (
                <div style={{ display: "flex" }}>
                  <Tag round intent="warning">
                    {t(ETLCodes.IsImporting)}
                  </Tag>
                </div>
              )}
              <SharepointButton onClick={() => {}} />
            </div>
          }
        >
          <FieldGroup columns={2}>
            <FGCustomInput name="requestStateId" label={t(ETLCodes.RequestNumber)}>
              {(ctx) => (
                <Link
                  target="_blank"
                  to={`${ERoutes.competitionRequest}/${ctx.formik.values?.competitionRequestId}/detail/view`}
                >
                  {ctx.formik.values.competitionRequest.request.requestNumber}
                </Link>
              )}
            </FGCustomInput>
            <FGSelectInput
              name="competitionStatusId"
              label={t(ETLCodes.Status)}
              items={competitionStatuses}
              loading={csloading}
              onItemViewClick={(item) => openNewTab(`${ERoutes.club}/${item}/detail/view`)}
            />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGPeopleSearchInput
              name="competitionRequest.request.personId"
              label={t(ETLCodes.Person)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              showWarning={true}
            />
            <EmailButton email={data?.competitionRequest?.clubEmail} />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGSelectInput
              name="competitionRequest.clubId"
              label={t(ETLCodes.Club)}
              items={clubs}
              loading={cloading}
              onItemViewClick={(item) => openNewTab(`${ERoutes.club}/${item}/detail/view`)}
              readonly
            />
          </FieldGroup>

          <FieldGroup columns={3}>
            <FGSRSHDateMaskInput name="competitionRequest.dateFrom" label={t(ETLCodes.DateFrom)} requiredMark />
            <FGSRSHDateMaskInput name="competitionRequest.dateTo" label={t(ETLCodes.DateTo)} requiredMark />
            <FGSRSHDateMaskInput
              name="competitionRequest.catalogReceptionDate"
              label={t(ETLCodes.CatalogReception)}
              readonly
            />
            <FGSelectInput
              name="competitionRequest.sectionId"
              label={t(ETLCodes.Section)}
              items={sections}
              loading={sloading}
              onItemViewClick={(item) => openNewTab(`${ERoutes.section}/${item}/detail/view`)}
              readonly
            />
            <FGSelectInput
              name="competitionRequest.activityId"
              label={t(ETLCodes.Activity)}
              items={activities}
              loading={aloading}
              onItemViewClick={(item) => openNewTab(`${ERoutes.activity}/${item}/detail/view`)}
              readonly
            />
            <FGNumberInput
              name="competitionRequest.numberParticipants"
              label={t(ETLCodes.ParticipantsCount)}
              readonly
            />
            <FGSelectInput
              name="competitionRequest.subActivityId"
              label={t(ETLCodes.SubActivity)}
              items={subActivities}
              loading={saloading}
              onItemViewClick={(item) => openNewTab(`${ERoutes.subActivity}/${item}/detail/view`)}
              readonly
            />
            <FGTextInput name="competitionRequest.extra" label={t(ETLCodes.Extra)} readonly />
            <FGEmpty />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGTextInput name="competitionRequest.clubAddress" label={t(ETLCodes.ClubAddress)} readonly />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGSRSHCheckboxInput name="competitionRequest.otherAddress" label={t(ETLCodes.OtherAddress)} />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGCountrySelect
              name="competitionRequest.countryId"
              label={t(ETLCodes.Country)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.country}/${item}/detail/view`)}
              visible={otherCityChecked}
            />
          </FieldGroup>
          <FieldGroup columns={2}>
            {isBelgium ? (
              <FGSelectInput
                name="competitionRequest.postalCodeId"
                label={t(ETLCodes.City)}
                items={cities}
                loading={pcloading}
                requiredMark={otherCityChecked}
                visible={otherCityChecked}
              />
            ) : (
              <FGTextInput name="competitionRequest.foreignCity" label={t(ETLCodes.City)} visible={otherCityChecked} />
            )}
          </FieldGroup>
        </FieldSet>
        <FieldSet title={t(ETLCodes.Judges)}>
          <FGCustomPanel>
            {({ formik }) => (
              <>
                {transientJudgeError && (
                  <FieldGroup columns={3}>
                    <FGEmpty />
                    <FGCustomPanel>
                      {(ctx) => (
                        <div style={{ color: "RED" }}>
                          <i className="fas fa-exclamation-triangle" />
                          {t(ETLCodes.OneOrMoreJudgeCannotJudgeThisSubActivity)}
                        </div>
                      )}
                    </FGCustomPanel>
                    <FGEmpty />
                  </FieldGroup>
                )}
                <CompetitionJudgeSelection
                  linkedIds={formik?.values?.competitionRequest.judgeIds || null}
                  onLinkedIdsChanged={(ids) => formik?.setFieldValue("competitionRequest.judgeIds", ids)}
                  editMode={editMode}
                  loading={loading}
                />
              </>
            )}
          </FGCustomPanel>
        </FieldSet>
      </SmallFormGenerator>
      <CompetitionResultList
        competitionId={competitionId}
        editMode={editMode}
        competitionRequestId={data?.competitionRequestId}
      />
    </>
  );
};
